import type { ComponentType } from "react";
import { useEffect } from "react";

import { ChatStateProvider } from "../features/chat/chat-state.provider.js";
import { DesktopBridgeRoomProvider } from "../features/desktop-bridge/desktop-bridge-room.provider.js";
import { useDesktopBridge } from "../features/desktop-bridge/use-desktop-bridge.js";
import { FluidProvider } from "../features/fluid/fluid.provider.js";
import { DESKTOP_APP_NOTIFICATION_KEY } from "../features/notifications/notification-keys.js";
import { useNotificationSettingsStore } from "../features/notifications/notification-settings.store.js";
import { useTableJoinChime } from "../features/notifications/use-table-join-chime.js";
import { OfficeProvider } from "../features/office/office-provider.js";
import { PresenceProvider } from "../features/presence/presence.provider.js";
import { TableCallProvider } from "../features/table-call/table-call.provider.js";

const TableJoinChimeListener = () => {
  useTableJoinChime();
  return null;
};

export const withFluidFramework = <P extends object>(
  Component: ComponentType<P>
) =>
  function WithFluidFrameworkComponent(props: P) {
    const {
      connect: connectBridge,
      disconnect: disconnectBridge,
      status: bridgeStatus,
    } = useDesktopBridge();
    const { getNotificationAction, setNotificationAction } =
      useNotificationSettingsStore();

    // Keep the bridge connected for the whole time the office route is active.
    // This also recovers after an explicit disconnect that happened before
    // returning to the office in the same app session.
    useEffect(() => {
      if (bridgeStatus === "idle" || bridgeStatus === "disconnected") {
        connectBridge();
      }
    }, [bridgeStatus, connectBridge]);

    useEffect(
      () => () => {
        disconnectBridge();
      },
      [disconnectBridge]
    );

    useEffect(() => {
      const handlePageTeardown = () => {
        disconnectBridge();
      };

      window.addEventListener("pagehide", handlePageTeardown);
      window.addEventListener("beforeunload", handlePageTeardown);

      return () => {
        window.removeEventListener("pagehide", handlePageTeardown);
        window.removeEventListener("beforeunload", handlePageTeardown);
      };
    }, [disconnectBridge]);

    useEffect(() => {
      if (bridgeStatus !== "ready") {
        return;
      }

      if (
        getNotificationAction(DESKTOP_APP_NOTIFICATION_KEY) === "doNotShowAgain"
      ) {
        return;
      }

      setNotificationAction(DESKTOP_APP_NOTIFICATION_KEY, "doNotShowAgain");
    }, [bridgeStatus, getNotificationAction, setNotificationAction]);

    return (
      <OfficeProvider>
        <FluidProvider>
          <TableJoinChimeListener />
          <DesktopBridgeRoomProvider>
            <PresenceProvider>
              <ChatStateProvider>
                <TableCallProvider>
                  <Component {...props} />
                </TableCallProvider>
              </ChatStateProvider>
            </PresenceProvider>
          </DesktopBridgeRoomProvider>
        </FluidProvider>
      </OfficeProvider>
    );
  };
